import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Globe, Camera, Clock, UserCheck, MessageSquare } from "lucide-react";
import { Question } from "@/types/test";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { TestScheduleSettings } from "@/components/test-creator/TestScheduleSettings";
interface ReviewScreenProps {
  testDetails: any;
  questions: Question[];
  onBack: () => void;
  onPublish: () => void;
  onSaveDraft: () => void;
  isPublishing: boolean;
  isSaving: boolean;
}
export function ReviewScreen({
  testDetails,
  questions,
  onBack,
  onPublish,
  isPublishing,
  isSaving
}: ReviewScreenProps) {
  const [publishToJobBoard, setPublishToJobBoard] = React.useState(testDetails.publishToJobBoard || false);
  const [webcamMonitoring, setWebcamMonitoring] = React.useState(testDetails.webcamMonitoringEnabled ?? true);
  const [invitationOnly, setInvitationOnly] = React.useState(testDetails.invitationOnly ?? false);
  const [welcomeMessage, setWelcomeMessage] = React.useState(testDetails.welcomeMessage || "");
  const [completionMessage, setCompletionMessage] = React.useState(testDetails.completionMessage || "");
  const [scheduleSettings, setScheduleSettings] = React.useState({
    isScheduled: testDetails.isScheduled ?? false,
    scheduledStartTime: testDetails.scheduledStartTime || null,
    scheduledEndTime: testDetails.scheduledEndTime || null,
    timezone: testDetails.scheduleTimezone || 'UTC',
  });
  
  const totalMarks = questions.reduce((sum, q) => sum + (q.marks || 1), 0);
  const totalTime = questions.reduce((sum, q) => sum + (q.time_limit || 0), 0);
  return <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Test Settings</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack} disabled={isPublishing || isSaving}>
            Back
          </Button>
          <Button onClick={() => {
            // Update test details with all current settings before publishing
            testDetails.publishToJobBoard = publishToJobBoard;
            testDetails.webcamMonitoringEnabled = webcamMonitoring;
            testDetails.invitationOnly = invitationOnly;
            testDetails.welcomeMessage = welcomeMessage;
            testDetails.completionMessage = completionMessage;
            testDetails.isScheduled = scheduleSettings.isScheduled;
            testDetails.scheduledStartTime = scheduleSettings.scheduledStartTime;
            testDetails.scheduledEndTime = scheduleSettings.scheduledEndTime;
            testDetails.scheduleTimezone = scheduleSettings.timezone;
            onPublish();
          }} disabled={isSaving || isPublishing} className="bg-[#e16702]">
            {isPublishing ? <span className="flex items-center gap-2">
                <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                Publishing...
              </span> : 'Publish Test'}
          </Button>
        </div>
      </div>

      {/* Publish to Job Board Toggle */}
      <Card className="relative overflow-hidden">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-secondary/50 to-primary/50 animate-pulse rounded-lg blur-sm"></div>
        <div className="relative bg-card rounded-lg p-4">
          <CardHeader>
            <CardTitle className="text-card-foreground flex items-center gap-2">
              <Globe className="h-5 w-5 text-muted-foreground" />
              Publish to Job Board
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between space-x-2">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Make this test available in the public job marketplace to attract qualified candidates.
                </p>
              </div>
              <Switch id="publish-job-board" checked={publishToJobBoard} onCheckedChange={checked => {
              setPublishToJobBoard(checked);
              // Update the test details with the new value
              testDetails.publishToJobBoard = checked;
            }} className="scale-110" />
            </div>
          </CardContent>
        </div>
      </Card>

      {/* Webcam Monitoring Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="text-card-foreground flex items-center gap-2">
            <Camera className="h-5 w-5 text-muted-foreground" />
            Webcam Monitoring
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Enable webcam monitoring to capture snapshots during the test for integrity verification.
              </p>
            </div>
            <Switch 
              id="webcam-monitoring" 
              checked={webcamMonitoring} 
              onCheckedChange={(checked) => {
                setWebcamMonitoring(checked);
                testDetails.webcamMonitoringEnabled = checked;
              }} 
              className="scale-110" 
            />
          </div>
        </CardContent>
      </Card>

      {/* Test Scheduling */}
      <Card>
        <CardHeader>
          <CardTitle className="text-card-foreground flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            Live Test Scheduling
          </CardTitle>
        </CardHeader>
        <CardContent>
          <TestScheduleSettings
            isScheduled={scheduleSettings.isScheduled}
            scheduledStartTime={scheduleSettings.scheduledStartTime}
            scheduledEndTime={scheduleSettings.scheduledEndTime}
            timezone={scheduleSettings.timezone}
            onScheduleChange={(data) => {
              setScheduleSettings(data);
              testDetails.isScheduled = data.isScheduled;
              testDetails.scheduledStartTime = data.scheduledStartTime;
              testDetails.scheduledEndTime = data.scheduledEndTime;
              testDetails.scheduleTimezone = data.timezone;
            }}
          />
        </CardContent>
      </Card>

      {/* Invitation Only */}
      <Card>
        <CardHeader>
          <CardTitle className="text-card-foreground flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-muted-foreground" />
            Invitation Only
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Restrict test access to only candidates who are in the invitation list. Others will see an ineligibility message.
              </p>
            </div>
            <Switch 
              id="invitation-only" 
              checked={invitationOnly} 
              onCheckedChange={(checked) => {
                setInvitationOnly(checked);
                testDetails.invitationOnly = checked;
              }} 
              className="scale-110" 
            />
          </div>
        </CardContent>
      </Card>

      {/* Custom Messages */}
      <Card>
        <CardHeader>
          <CardTitle className="text-card-foreground flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            Custom Messages
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Welcome Message */}
          <div className="space-y-2">
            <Label htmlFor="welcome-message" className="text-sm font-medium">
              Welcome Message (Optional)
            </Label>
            <p className="text-xs text-muted-foreground">
              This message will be shown to candidates before they start the test, below the default test instructions.
            </p>
            <Textarea
              id="welcome-message"
              placeholder="e.g., We're excited to have you take this assessment. Please ensure you're in a quiet environment..."
              value={welcomeMessage}
              onChange={(e) => {
                setWelcomeMessage(e.target.value);
                testDetails.welcomeMessage = e.target.value;
              }}
              className="min-h-[100px]"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">
              {welcomeMessage.length}/500 characters
            </p>
          </div>

          {/* Completion Message */}
          <div className="space-y-2">
            <Label htmlFor="completion-message" className="text-sm font-medium">
              Completion Message (Optional)
            </Label>
            <p className="text-xs text-muted-foreground">
              This message will be shown to candidates on the thank you page after they complete the test.
            </p>
            <Textarea
              id="completion-message"
              placeholder="e.g., Thank you for completing our assessment! We'll review your responses and get back to you within 3-5 business days..."
              value={completionMessage}
              onChange={(e) => {
                setCompletionMessage(e.target.value);
                testDetails.completionMessage = e.target.value;
              }}
              className="min-h-[100px]"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">
              {completionMessage.length}/500 characters
            </p>
          </div>
        </CardContent>
      </Card>
    </div>;
}